import { useState, useEffect } from "react";
import { Star, ThumbsUp, CheckCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReviewStars = ({ rating, size = "w-5 h-5", interactive = false, onRate }) => {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          onClick={() => interactive && onRate?.(star)}
          className={interactive ? "cursor-pointer hover:scale-110 transition-transform" : "cursor-default"}
          disabled={!interactive}
        >
          <Star
            className={`${size} ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "fill-transparent text-white/30"
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const ProductReviews = ({ productType = "wood" }) => {
  const { user, token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ average_rating: 0, total_reviews: 0 });
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [newReview, setNewReview] = useState({
    rating: 5,
    title: "",
    content: ""
  });

  useEffect(() => {
    fetchReviews();
  }, [productType]);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews?product_type=${productType}`);
      setReviews(response.data.reviews);
      setStats(response.data.stats);
    } catch (error) {
      console.error("Failed to fetch reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please login to leave a review");
      return;
    }

    if (!newReview.title.trim() || !newReview.content.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        product_type: productType,
        rating: newReview.rating,
        title: newReview.title,
        content: newReview.content
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success("Review submitted! Thank you for your feedback.");
      setShowForm(false);
      setNewReview({ rating: 5, title: "", content: "" });
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const productNames = {
    wood: "Wooden Enclosures",
    pvc: "PVC Enclosures",
    stacking: "Stacking Units"
  };

  return (
    <div className="bg-[#121212] border border-white/10 p-6" data-testid="product-reviews">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h3 className="text-xl font-bold text-[#F4F1DE] mb-2">
            Customer Reviews
          </h3>
          <div className="flex items-center gap-3">
            <ReviewStars rating={Math.round(stats.average_rating)} />
            <span className="text-[#F4F1DE]/80">
              {stats.average_rating > 0 ? stats.average_rating.toFixed(1) : "No"} 
              {stats.average_rating > 0 && " out of 5"}
            </span>
            <span className="text-[#F4F1DE]/50">
              ({stats.total_reviews} review{stats.total_reviews !== 1 ? "s" : ""})
            </span>
          </div>
        </div>
        
        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white"
            data-testid="write-review-btn"
          >
            Write a Review
          </Button>
        )}
      </div>

      {/* Review Form */}
      {showForm && (
        <form onSubmit={handleSubmitReview} className="bg-[#0A0A0A] border border-white/5 p-6 rounded-lg mb-6">
          <h4 className="text-lg font-bold text-[#F4F1DE] mb-4">
            Review {productNames[productType]}
          </h4>
          
          <div className="mb-4">
            <Label className="text-[#F4F1DE]/80 mb-2 block">Your Rating</Label>
            <ReviewStars
              rating={newReview.rating}
              size="w-8 h-8"
              interactive
              onRate={(rating) => setNewReview(prev => ({ ...prev, rating }))}
            />
          </div>

          <div className="mb-4">
            <Label htmlFor="review-title" className="text-[#F4F1DE]/80">Review Title</Label>
            <Input
              id="review-title"
              value={newReview.title}
              onChange={(e) => setNewReview(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Summarize your experience"
              className="mt-2 bg-[#121212] border-white/10 text-[#F4F1DE]"
              data-testid="review-title"
            />
          </div>

          <div className="mb-4">
            <Label htmlFor="review-content" className="text-[#F4F1DE]/80">Your Review</Label>
            <textarea
              id="review-content"
              value={newReview.content}
              onChange={(e) => setNewReview(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Tell us what you liked or didn't like..."
              className="mt-2 w-full h-24 bg-[#121212] border border-white/10 text-[#F4F1DE] p-3 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-[#E07A5F]/50"
              data-testid="review-content"
            />
          </div>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowForm(false)}
              className="border-white/20 text-[#F4F1DE]/80"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white"
              data-testid="submit-review-btn"
            >
              {submitting ? <span className="spinner" /> : "Submit Review"}
            </Button>
          </div>
        </form>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="text-center py-8">
          <span className="spinner" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-8 text-[#F4F1DE]/50">
          <p>No reviews yet. Be the first to review!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div key={review.id} className="border-b border-white/5 pb-4 last:border-0">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <ReviewStars rating={review.rating} size="w-4 h-4" />
                    <span className="font-medium text-[#F4F1DE]">{review.title}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[#F4F1DE]/50">
                    <span>{review.user_name}</span>
                    {review.verified_purchase && (
                      <span className="flex items-center gap-1 text-green-400">
                        <CheckCircle className="w-3 h-3" />
                        Verified Purchase
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-[#F4F1DE]/40 text-xs">
                  {new Date(review.created_at).toLocaleDateString()}
                </span>
              </div>
              <p className="text-[#F4F1DE]/80 text-sm">{review.content}</p>

              {/* Photos */}
              {review.photos && review.photos.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid={`review-photos-${review.id}`}>
                  {review.photos.map((pid) => (
                    <a
                      key={pid}
                      href={`${API}/reviews/photo/${pid}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-20 h-20 border border-white/10 overflow-hidden bg-[#0A0A0A] hover:border-[#E07A5F]/60 transition-colors"
                    >
                      <img
                        src={`${API}/reviews/photo/${pid}`}
                        alt=""
                        loading="lazy"
                        onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                        className="w-full h-full object-cover"
                      />
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductReviews;
